import moment from 'moment';
import { getFixedValue, convert } from '../currencyConvertion';
import messages from '../../locale/messages';
import { getServiceFeeValue } from '../getServiceFeeValue';

// Helper function to calculate custom discount
const calculateCustomDiscount = (customDiscounts, dayDifference, formatMessage) => {
	if (!customDiscounts) return { discount: 0, discountType: null };

	try {
		const discounts = JSON.parse(customDiscounts);
		if (!Array.isArray(discounts) || discounts.length === 0) {
			return { discount: 0, discountType: null };
		}

		// Convert all periods to nights for comparison
		const eligibleDiscounts = discounts
			.map(discount => {
				let periodInNights = 0;
				switch (discount.periodType) {
					case 'nights':
						periodInNights = parseInt(discount.periodValue);
						break;
					case 'weeks':
						periodInNights = parseInt(discount.periodValue) * 7;
						break;
					case 'months':
						periodInNights = parseInt(discount.periodValue) * 28;
						break;
					default:
						return null;
				}

				return {
					...discount,
					periodInNights,
					discountPercentage: parseFloat(discount.discountPercentage)
				};
			})
			.filter(discount =>
				discount &&
				discount.periodInNights <= dayDifference &&
				discount.discountPercentage > 0
			);

		if (eligibleDiscounts.length === 0) {
			return { discount: 0, discountType: null };
		}

		// Find the discount with the longest period (highest nights requirement)
		const bestDiscount = eligibleDiscounts.reduce((best, current) =>
			current.periodInNights > best.periodInNights ? current : best
		);

		// Generate discount type label
		let discountType;
		if (bestDiscount.periodType === 'nights') {
			const nightsLabel = formatMessage
				? (bestDiscount.periodValue > 1 ? formatMessage(messages.nights) : formatMessage(messages.night))
				: (bestDiscount.periodValue > 1 ? 'nights' : 'night');
			const discountLabel = formatMessage ? formatMessage(messages.discount) : 'Discounts';
			discountType = `${bestDiscount.discountPercentage}% ${bestDiscount.periodValue} ${nightsLabel} ${discountLabel}`;
		} else if (bestDiscount.periodType === 'weeks') {
			const weeklyLabel = formatMessage ? formatMessage(messages.weeklyDiscount) : 'Weekly Discount';
			discountType = `${bestDiscount.discountPercentage}% ${weeklyLabel}`;
		} else if (bestDiscount.periodType === 'months') {
			const monthlyLabel = formatMessage ? formatMessage(messages.monthlyDiscount) : 'Monthly Discount';
			discountType = `${bestDiscount.discountPercentage}% ${monthlyLabel}`;
		}

		return {
			discount: bestDiscount.discountPercentage,
			discountType
		};

	} catch (e) {
		console.log('Error parsing customDiscounts:', e);
		return { discount: 0, discountType: null };
	}
};

// Helper function to check if a date is a weekend (Friday or Saturday night)
const isWeekendNight = (date) => {
	const dayOfWeek = date.day(); // 0 = Sunday, 5 = Friday, 6 = Saturday
	return dayOfWeek === 5 || dayOfWeek === 6; // Friday or Saturday nights
};

export function getCalculationDetails({ startDate, endDate, serviceFees, specialPricing, currency,
	toCurrency, basePrice, cleaningPrice, monthlyDiscount, weeklyDiscount, customDiscounts, formatMessage, base, rates, tax, extraGuestThreshold, extraGuestFee, guests, weekendPrice }) {

	let taxes = 0, totalWithoutTax = 0, bookingSpecialPricing = [], isSpecialPriceAssigned = false;
	let totalWithoutServiceFee = 0, priceForDays = 0, isAverage = 0, isDayTotal = 0, total = 0, discount = 0, totalIncludeTax = 0;
	let momentStartDate, momentEndDate, dayDifference, discountType, oneTotalPrice = 0;
	let extraGuestFeeAmount = 0, extraGuestCount = 0;

	if (startDate && endDate) {
		startDate = moment(startDate).format('YYYY-MM-DD');
		momentStartDate = moment(startDate);
		momentEndDate = moment(endDate);
		dayDifference = momentEndDate.diff(momentStartDate, 'days');
		cleaningPrice = Math.ceil(cleaningPrice);
		basePrice = Math.ceil(basePrice);
		monthlyDiscount = getFixedValue(monthlyDiscount);
		weeklyDiscount = getFixedValue(weeklyDiscount);

		if (dayDifference > 0) {
			let stayedNights = [];
			// Find stayed nights
			for (let i = 0; i < dayDifference; i++) {
				let currentDate = moment(startDate).add(i, 'day');
				stayedNights.push(currentDate);
			}

			if (stayedNights?.length > 0) {
				stayedNights.map((item) => {
					let isSpecialPricing;
					if (item) {
						let pricingRow, currentPrice;
						isSpecialPricing = specialPricing?.length > 0 && specialPricing?.find(o => moment(item).format('DD/MM/YYYY') == moment(o.blockedDates, 'DD/MM/YYYY').format('DD/MM/YYYY'));
						if (isSpecialPricing?.isSpecialPrice && (isSpecialPricing?.dayStatus == 'secondHalf' || isSpecialPricing?.dayStatus == 'full')) {
							// Priority 1: Special pricing from calendar
							isSpecialPriceAssigned = true;
							currentPrice = Math.ceil(Number(isSpecialPricing?.isSpecialPrice));
						} else if (weekendPrice && isWeekendNight(item)) {
							// Priority 2: Weekend pricing (Friday/Saturday nights)
							isSpecialPriceAssigned = true;
							currentPrice = Math.ceil(Number(weekendPrice));
						} else {
							// Priority 3: Base price
							currentPrice = Math.ceil(Number(basePrice));
						}
						currentPrice = Math.ceil(getFixedValue(currentPrice));
						pricingRow = {
							blockedDates: item,
							isSpecialPrice: currentPrice,
						};
						bookingSpecialPricing.push(pricingRow);
					}
				});
			}
		}

		priceForDays = bookingSpecialPricing.reduce(
			(total, item) => total + Number(item.isSpecialPrice), 0
		);
		priceForDays = Math.ceil(getFixedValue(priceForDays));
		discount = 0;
		discountType = null;
		total = 0;
	}

	isAverage = Math.ceil(Number(priceForDays) / Number(dayDifference));
	isDayTotal = isAverage * dayDifference;
	priceForDays = isDayTotal;

	// Calculate discount using custom discounts first, fallback to legacy weekly/monthly
	if (customDiscounts) {
		const customDiscountResult = calculateCustomDiscount(customDiscounts, dayDifference, formatMessage);
		if (customDiscountResult.discount > 0) {
			discount = (Number(priceForDays) * Number(customDiscountResult.discount)) / 100;
			discountType = customDiscountResult.discountType;
			discount = Math.ceil(discount);
		}
	} else {
		// Legacy discount logic for backward compatibility
		if (dayDifference >= 7) {
			if (monthlyDiscount > 0 && dayDifference >= 28) {
				discount = (Number(priceForDays) * Number(monthlyDiscount)) / 100;
				discountType = formatMessage
					? monthlyDiscount + "% " + formatMessage(messages.monthlyDiscount)
					: monthlyDiscount + "% Monthly Discount";
			} else {
				discount = (Number(priceForDays) * Number(weeklyDiscount)) / 100;
				discountType = formatMessage
					? weeklyDiscount + "% " + formatMessage(messages.weeklyDiscount)
					: weeklyDiscount + "% Weekly Discount";
			}
			discount = Math.ceil(getFixedValue(discount));
		}
	}

	// Calculate extra guest fees
	// extraGuestThreshold = number of guests included in base price
	// Extra fees apply only for guests beyond this threshold (flat fee per stay)
	if (extraGuestThreshold && extraGuestFee && guests && dayDifference > 0) {
		extraGuestThreshold = Number(extraGuestThreshold);
		extraGuestFee = Number(extraGuestFee);
		guests = Number(guests);

		if (guests > extraGuestThreshold) {
			extraGuestCount = guests - extraGuestThreshold;
			extraGuestFeeAmount = extraGuestCount * extraGuestFee * dayDifference;
			extraGuestFeeAmount = Math.ceil(getFixedValue(extraGuestFeeAmount));
		}
	}

	totalWithoutServiceFee = (priceForDays + cleaningPrice + extraGuestFeeAmount) - discount;
	totalWithoutTax = priceForDays - discount;

	const { guestServiceFee: rawGuestServiceFee, hostServiceFee: rawHostServiceFee, guestServiceFeeType, hostServiceFeeType, guestServiceFeeValue, hostServiceFeeValue } = getServiceFeeValue({ serviceFees, totalWithoutServiceFee, base, rates, currency });

	// Apply ceiling to service fees
	const guestServiceFee = Math.ceil(rawGuestServiceFee);
	const hostServiceFee = Math.ceil(rawHostServiceFee);

	if (tax) taxes = totalWithoutTax * (Number(getFixedValue(tax)) / 100);
	taxes = Math.ceil(getFixedValue(taxes));
	total = getFixedValue((priceForDays + guestServiceFee + cleaningPrice + taxes + extraGuestFeeAmount) - discount);
	totalIncludeTax = getFixedValue((priceForDays + cleaningPrice + taxes + extraGuestFeeAmount) - discount);
	oneTotalPrice = getFixedValue((priceForDays + guestServiceFee + cleaningPrice + extraGuestFeeAmount) - discount);

	if (toCurrency && currency != toCurrency) {
		let priceForDaysConvert, serviceFeeConvert, cleaningPriceConvert, taxesConvert, discountConvert, extraGuestFeeConvert;
		priceForDaysConvert = Math.ceil(getFixedValue(convert(base, rates, priceForDays, currency, toCurrency)));
		serviceFeeConvert = Math.ceil(getFixedValue(convert(base, rates, guestServiceFee, currency, toCurrency)));
		cleaningPriceConvert = Math.ceil(getFixedValue(convert(base, rates, cleaningPrice, currency, toCurrency)));
		taxesConvert = Math.ceil(getFixedValue(convert(base, rates, taxes, currency, toCurrency)));
		discountConvert = Math.ceil(getFixedValue(convert(base, rates, discount, currency, toCurrency)));
		extraGuestFeeConvert = Math.ceil(getFixedValue(convert(base, rates, extraGuestFeeAmount, currency, toCurrency)));
		total = (priceForDaysConvert + serviceFeeConvert + cleaningPriceConvert + taxesConvert + extraGuestFeeConvert) - discountConvert;
		total = Math.ceil(convert(base, rates, total, toCurrency, currency));
	}

	
	return {
		taxes,
		totalWithoutTax,
		bookingSpecialPricing,
		isSpecialPriceAssigned,
		totalWithoutServiceFee,
		priceForDays,
		isAverage,
		total,
		discount,
		dayDifference,
		discountType,
		guestServiceFee,
		hostServiceFee,
		guestServiceFeeType,
		hostServiceFeeType,
		guestServiceFeeValue,
		hostServiceFeeValue,
		totalIncludeTax,
		oneTotalPrice,
		extraGuestFeeAmount,
		extraGuestCount
	};
};
